import React from 'react'
import {
  resolveDsnyCombineTarget,
  type DsnyCombineTarget,
} from '../../lib/contractServiceLineItems'
import { formatEmployeeLabel } from '../../lib/formatEmployeeLabel'

type AssignableRow = {
  id: string
  type: string
  combine_target?: DsnyCombineTarget
  combine_with_dsny?: boolean
  assigned_crew_id?: string | null
  assigned_employee_id?: string | null
}

type CrewOption = { id: string; name: string }
type EmployeeOption = {
  id: string
  first_name: string
  last_name: string
  employee_number?: string | null
}

interface DsnyVisitRepAssignmentProps {
  rows: AssignableRow[]
  crews: CrewOption[]
  employees: EmployeeOption[]
  onAssign: (
    rowIds: string[],
    patch: { assigned_crew_id: string | null } | { assigned_employee_id: string | null }
  ) => void
}

function AssignmentPair({
  title,
  hint,
  rowIds,
  sample,
  crews,
  employees,
  onAssign,
}: {
  title: string
  hint: string
  rowIds: string[]
  sample: AssignableRow | undefined
  crews: CrewOption[]
  employees: EmployeeOption[]
  onAssign: DsnyVisitRepAssignmentProps['onAssign']
}) {
  if (rowIds.length === 0) return null
  return (
    <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
      <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
      <p className="text-xs text-gray-600 mt-1 mb-3">{hint}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Crew</label>
          <select
            value={sample?.assigned_crew_id || ''}
            onChange={(e) =>
              onAssign(rowIds, { assigned_crew_id: e.target.value || null })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">Inherit from contract / property</option>
            {sample?.assigned_crew_id &&
              !crews.some((c) => c.id === sample.assigned_crew_id) && (
                <option value={sample.assigned_crew_id}>Assigned crew</option>
              )}
            {crews.map((crew) => (
              <option key={crew.id} value={crew.id}>
                {crew.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Employee / Rep</label>
          <select
            value={sample?.assigned_employee_id || ''}
            onChange={(e) =>
              onAssign(rowIds, { assigned_employee_id: e.target.value || null })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">Inherit from contract / property</option>
            {sample?.assigned_employee_id &&
              !employees.some((e) => e.id === sample.assigned_employee_id) && (
                <option value={sample.assigned_employee_id}>Assigned employee</option>
              )}
            {employees.map((employee) => (
              <option key={employee.id} value={employee.id}>
                {formatEmployeeLabel(employee)}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  )
}

/** Top-of-schedule Exterior (DSNY) + Interior (hallway) assignee dropdowns. */
export function DsnyVisitRepAssignment({
  rows,
  crews,
  employees,
  onAssign,
}: DsnyVisitRepAssignmentProps) {
  const exteriorRows = rows.filter((row) => resolveDsnyCombineTarget(row) === 'dsny')
  const interiorRows = rows.filter((row) => resolveDsnyCombineTarget(row) === 'hallway')

  if (exteriorRows.length === 0 && interiorRows.length === 0) return null

  return (
    <div className="space-y-3">
      <AssignmentPair
        title="Exterior / DSNY visits assignment"
        hint="One crew/rep shared by all rows targeting DSNY Exterior."
        rowIds={exteriorRows.map((r) => r.id)}
        sample={
          exteriorRows.find((r) => r.assigned_crew_id || r.assigned_employee_id) ||
          exteriorRows[0]
        }
        crews={crews}
        employees={employees}
        onAssign={onAssign}
      />
      <AssignmentPair
        title="Interior / Hallway visits assignment"
        hint="One crew/rep shared by all rows targeting Hallway / Interior visits."
        rowIds={interiorRows.map((r) => r.id)}
        sample={
          interiorRows.find((r) => r.assigned_crew_id || r.assigned_employee_id) ||
          interiorRows[0]
        }
        crews={crews}
        employees={employees}
        onAssign={onAssign}
      />
    </div>
  )
}

export function visitTargetSelectValue(
  row: Pick<AssignableRow, 'type' | 'combine_target' | 'combine_with_dsny'>
): string {
  const t = resolveDsnyCombineTarget(row)
  if (t === undefined) return ''
  if (t === null) return 'separate'
  return t
}

export function visitTargetFromSelectValue(value: string): DsnyCombineTarget | undefined {
  if (value === '') return undefined
  if (value === 'separate') return null
  if (value === 'dsny' || value === 'hallway') return value
  return undefined
}
